/**
 * @file promotion-base.js
 * @description Base class for all promotion types
 * @module GniibPromotions.PromotionBase
 *
 * Contains the core promotion logic shared across all promotion types.
 * Specific promotion handlers override methods to customize behavior.
 */
(function(global) {
  'use strict';

  var GP = global.GniibPromotions = global.GniibPromotions || {};
  var Utils = GP.Utils;
  var Config = GP.Config;
  var CustomerService = GP.CustomerService;
  var CartManager = GP.CartManager;

  /**
   * Base Promotion Class
   * @class PromotionBase
   * @param {Object} handler - Promotion-specific handler object
   */
  function PromotionBase(handler) {
    this.handler = handler || {};
    this.cart = CartManager.create();
    this.elements = {};
    this.customerRole = null;
    this.promotionType = null;
    this.apiEndpoint = null;

    // SF Express state
    this.enableSFExpressIntegration = Config.SF_EXPRESS.ENABLED;
    this.sfExpressHandlers = null;
    this.navigationBlocker = null;
  }

  /**
   * Initialize the promotion
   * @async
   */
  PromotionBase.prototype.init = async function() {
    try {
      this.elements = Utils.getDomElements();
      this.bindEvents();
      Utils.setupAuthButtons(this.elements.LOGIN_BTN, this.elements.REGISTER_BTN);

      await this.loadPromotionData();

      // Run handler-specific period/deadline validation
      if (this.handler.validatePeriod) {
        this.handler.validatePeriod(this);
      }

      await this.loadProducts();

      // Render campaigns if handler supports it
      if (this.handler.renderCampaigns) {
        this.handler.renderCampaigns(this);
      }

      this.renderProducts();
      this.updateSummary();
      this.showContent();
    } catch (error) {
      console.error('Failed to initialize promotion:', error);
      this.showError(error.message);
    }
  };

  /**
   * Bind event listeners
   */
  PromotionBase.prototype.bindEvents = function() {
    var self = this;

    if (this.elements.CHECKOUT_BTN) {
      this.elements.CHECKOUT_BTN.addEventListener('click', function() {
        self.handleCheckout();
      });
    }

    if (this.elements.RETRY_BTN) {
      this.elements.RETRY_BTN.addEventListener('click', function() {
        self.showLoading();
        self.init();
      });
    }

    // Mobile summary events
    if (this.elements.SUMMARY_COMPACT_TOGGLE) {
      this.elements.SUMMARY_COMPACT_TOGGLE.addEventListener('click', function() {
        GP.UI.MobileSummary.toggle(self);
      });
    }

    if (this.elements.SUMMARY_CLOSE_BTN) {
      this.elements.SUMMARY_CLOSE_BTN.addEventListener('click', function() {
        GP.UI.MobileSummary.close(self);
      });
    }

    if (this.elements.SUMMARY_BACKDROP) {
      this.elements.SUMMARY_BACKDROP.addEventListener('click', function() {
        GP.UI.MobileSummary.close(self);
      });
    }

    if (this.elements.MOBILE_CHECKOUT_BTN) {
      this.elements.MOBILE_CHECKOUT_BTN.addEventListener('click', function() {
        self.handleCheckout();
      });
    }
  };

  /**
   * Load promotion data from API
   * @async
   */
  PromotionBase.prototype.loadPromotionData = async function() {
    var customerId = CustomerService.getCustomerId();
    var role = CustomerService.getCustomerRole();

    if (!customerId) {
      throw new Error(GP.Messages.ERRORS.LOGIN_REQUIRED);
    }

    if (!role) {
      throw new Error(GP.Messages.ERRORS.ROLE_NOT_FOUND);
    }

    this.customerRole = role.toLowerCase();
    this.promotionType = CustomerService.getPromotionType(this.customerRole);

    if (!this.promotionType) {
      throw new Error(GP.Messages.ERRORS.NOT_ELIGIBLE);
    }

    // Check SF Express eligibility
    this.enableSFExpressIntegration = CustomerService.isSFExpressEnabled(this.promotionType);

    // Store in cart for services to access
    this.cart.promotionType = this.promotionType;

    this.apiEndpoint = GP.ApiEndpoints.getEndpoint(this.promotionType);

    console.log('Role:', this.customerRole, 'Type:', this.promotionType);

    var response = await fetch(this.apiEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId: customerId,
        role: role
      })
    });

    if (!response.ok) {
      throw new Error('API request failed: ' + response.status);
    }

    var data = await response.json();
    console.log('Promotion data from server:', data);

    if (!data.eligible) {
      // Period-gated responses include periodInfo. Let the promotion-specific
      // validator show its localized date/time message before using the generic
      // eligibility fallback.
      if (data.periodInfo && this.handler.validatePeriod) {
        this.cart.promotionData = data;
        this.handler.validatePeriod(this);
      }

      throw new Error('Customer not eligible for this promotion');
    }

    this.cart.promotionData = data;
  };

  /**
   * Load products from Shopify API
   * @async
   */
  PromotionBase.prototype.loadProducts = async function() {
    // Use handler-specific product loading if available
    if (this.handler.loadProducts) {
      this.cart.productsData = await this.handler.loadProducts(this);
      return;
    }

    // Default: load products using includedProducts array
    var includedProducts = this.cart.promotionData?.promotion?.includedProducts;
    if (!includedProducts || includedProducts.length === 0) {
      throw new Error('No included products found');
    }

    var products = [];

    for (var i = 0; i < includedProducts.length; i++) {
      var productHandle = includedProducts[i];
      try {
        var response = await fetch('https://www.g-niib.com/products/' + productHandle + '.js');
        if (response.ok) {
          var product = await response.json();
          products.push(product);
        }
      } catch (error) {
        console.warn('Failed to load product ' + productHandle + ':', error);
      }
    }

    if (products.length === 0) {
      throw new Error('No products could be loaded');
    }

    this.cart.productsData = products;
  };

  /**
   * Render products grid
   */
  PromotionBase.prototype.renderProducts = function() {
    GP.UI.ProductRenderer.render(this);
  };

  /**
   * Update summary display
   */
  PromotionBase.prototype.updateSummary = function() {
    GP.UI.UsageDisplay.update(this);
    GP.UI.SummaryRenderer.updateSelectedItems(this);
    GP.UI.SummaryRenderer.updateSubtotal(this);
    GP.UI.SummaryRenderer.updateCheckoutButton(this);
    GP.UI.MobileSummary.update(this);
  };

  /**
   * Add item to selection
   * @param {string|number} productId - Product ID
   * @param {string|number} variantId - Variant ID
   * @param {number} quantity - Quantity to add
   */
  PromotionBase.prototype.addToSelection = function(productId, variantId, quantity) {
    var product = CartManager.findProduct(this.cart, productId);
    var variant = CartManager.findVariant(product, variantId);

    if (!product || !variant) return;

    // Use handler-specific add logic
    if (this.handler.addToSelection) {
      var result = this.handler.addToSelection(this, product, variant, quantity);
      if (!result) return; // Handler returned false, addition rejected
    } else {
      // Default add logic
      var itemKey = Utils.createItemKey(productId, variantId);
      var existingItem = CartManager.getItem(this.cart, itemKey);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        var item = {
          productId: productId,
          variantId: variantId,
          product: product,
          variant: variant,
          quantity: quantity,
          basePrice: variant.price,
          promotionType: this.promotionType
        };
        CartManager.setItem(this.cart, itemKey, item);
      }
    }

    // Recalculate pricing if handler provides method
    if (this.handler.recalculatePricing) {
      this.handler.recalculatePricing(this);
    }

    this.updateSummary();
    GP.UI.ProductRenderer.updateAvailability(this);

    // Track add to cart event
    if (GP.Analytics) {
      var item = CartManager.getItem(this.cart, Utils.createItemKey(productId, variantId));
      if (item) {
        GP.Analytics.trackAddToCart(
          product,
          variant,
          quantity,
          this.promotionType,
          item.discountedPrice || item.basePrice
        );
      }
    }
  };

  /**
   * Remove item from selection
   * @param {string} itemKey - Item key (productId-variantId)
   */
  PromotionBase.prototype.removeFromSelection = function(itemKey) {
    // Track remove from cart event before removing the item
    var item = CartManager.getItem(this.cart, itemKey);
    if (GP.Analytics && item) {
      GP.Analytics.trackRemoveFromCart(
        item.product,
        item.variant,
        item.quantity,
        this.promotionType
      );
    }

    CartManager.removeItem(this.cart, itemKey);

    // Recalculate pricing if handler provides method
    if (this.handler.recalculatePricing) {
      this.handler.recalculatePricing(this);
    }

    this.updateSummary();
    GP.UI.ProductRenderer.updateAvailability(this);
  };

  /**
   * Handle checkout button click
   * @async
   */
  PromotionBase.prototype.handleCheckout = async function() {
    console.log('[Checkout] handleCheckout called');

    try {
      if (!CartManager.hasItems(this.cart)) {
        console.log('[Checkout] Cart is empty, exiting');
        return;
      }

      // Run handler-specific pre-checkout validation
      if (this.handler.validateBeforeCheckout) {
        console.log('[Checkout] Running validateBeforeCheckout...');
        var isValid = this.handler.validateBeforeCheckout(this);
        console.log('[Checkout] validateBeforeCheckout returned:', isValid);
        if (!isValid) return;
      }

      console.log('[Checkout] SF Express enabled:', this.enableSFExpressIntegration);

      // Use checkout service
      if (this.enableSFExpressIntegration) {
        await GP.Checkout.handleWithSFExpress(this);
      } else {
        await GP.Checkout.proceedWithDraftOrder(this, null);
      }
    } catch (error) {
      console.error('[Checkout] Unhandled error in handleCheckout:', error);
      alert(Config.Messages.ERRORS.CHECKOUT_ERROR || '結帳過程中發生錯誤，請重試。');
      GP.Checkout.setButtonsState(this, false, 'ready');
    }
  };

  /**
   * Get discount application name for an item
   * @param {Object} item - Cart item
   * @returns {string} Discount name
   */
  PromotionBase.prototype.getDiscountNameForItem = function(item) {
    if (this.handler.getDiscountNameForItem) {
      return this.handler.getDiscountNameForItem(this, item);
    }
    return this.getDiscountName();
  };

  /**
   * Get default discount application name
   * @returns {string} Discount name
   */
  PromotionBase.prototype.getDiscountName = function() {
    if (this.handler.getDiscountName) {
      return this.handler.getDiscountName(this);
    }
    return GP.Messages.DISCOUNT_NAMES[this.promotionType] || '';
  };

  /**
   * Calculate total for a single item
   * @param {Object} item - Cart item
   * @returns {number} Item total in cents
   */
  PromotionBase.prototype.calculateItemTotal = function(item) {
    if (this.handler.calculateItemTotal) {
      return this.handler.calculateItemTotal(this, item);
    }

    // Default: use discounted price if available
    var price = item.discountedPrice || item.basePrice;
    return price * item.quantity;
  };

  /**
   * Show loading state
   */
  PromotionBase.prototype.showLoading = function() {
    if (this.elements.LOADING) this.elements.LOADING.style.display = 'flex';
    if (this.elements.ERROR) this.elements.ERROR.style.display = 'none';
    if (this.elements.CONTENT) this.elements.CONTENT.style.display = 'none';
  };

  /**
   * Show error state
   * @param {string} message - Error message
   */
  PromotionBase.prototype.showError = function(message) {
    message = message || GP.Messages.ERRORS.LOAD_ERROR;

    if (this.elements.LOADING) this.elements.LOADING.style.display = 'none';
    if (this.elements.ERROR) this.elements.ERROR.style.display = 'block';
    if (this.elements.CONTENT) this.elements.CONTENT.style.display = 'none';

    if (this.elements.ERROR_MESSAGE) {
      this.elements.ERROR_MESSAGE.textContent = message;
    }

    // Show appropriate buttons
    var isLoginError = message.includes('登入');
    var isAccessError = message.includes('role') || message.includes('qualify') || message.includes('staff members');

    if (this.elements.LOGIN_BTN) {
      this.elements.LOGIN_BTN.style.display = isLoginError ? 'inline-flex' : 'none';
    }
    if (this.elements.REGISTER_BTN) {
      this.elements.REGISTER_BTN.style.display = isLoginError ? 'inline-flex' : 'none';
    }
    if (this.elements.RETRY_BTN) {
      this.elements.RETRY_BTN.style.display = (isAccessError || isLoginError) ? 'none' : 'inline-block';
    }
  };

  /**
   * Show content state
   */
  PromotionBase.prototype.showContent = function() {
    if (this.elements.LOADING) this.elements.LOADING.style.display = 'none';
    if (this.elements.ERROR) this.elements.ERROR.style.display = 'none';
    if (this.elements.CONTENT) this.elements.CONTENT.style.display = 'block';
  };

  /**
   * Get promotion configuration
   * @returns {Object} Config for this promotion type
   */
  PromotionBase.prototype.getConfig = function() {
    if (this.handler.getConfig) {
      return this.handler.getConfig();
    }
    return {};
  };

  // Export
  GP.PromotionBase = PromotionBase;

})(window);
